import { Observable, timer } from "rxjs";
import { last, switchMap, take, takeWhile } from "rxjs/operators";

export type Config<T> = {
  polling_interval: number;
  max_attempts: number;
  take_while: (result: T) => boolean;
  due_time?: number;
  inclusive?: boolean;
};

/**
 * Polling function to repeatedly check a condition until it is met or a maximum number of attempts is reached.
 *
 * @template T - The type of the observable value.
 * @param {() => Observable<T>} check - A function that returns an observable to check the condition.
 * @param {Config<T>} config - Configuration object for the polling.
 * @param {number} [config.due_time=0] - Initial delay before starting the polling, in milliseconds.
 * @param {number} config.polling_interval - Interval between polling attempts, in milliseconds.
 * @param {number} config.max_attempts - Maximum number of polling attempts.
 * @param {(value: T) => boolean} config.take_while - Function to determine whether to continue polling based on the emitted value.
 * @param {boolean} [config.inclusive=false] - Whether to include the last value that caused the takeWhile condition to fail.
 * @returns {Observable<T>} - An observable that emits the result of the polling.
 *
 * @example
 * const checkStatus = () => this._documentsService.getFileStatus(file.id);
 * const config = {
 *   due_time: 0,
 *   polling_interval: 3000,
 *   max_attempts: 10,
 *   take_while: (status) => status !== 'processed',
 *   inclusive: true
 * };
 * usePolling(checkStatus, config).subscribe(
 *   (status) => console.log('Final status:', status),
 *   (error) => console.error('Polling error:', error)
 * );
 */
export function usePolling<T = any>(check: () => Observable<T>, config: Config<T>): Observable<T> {
  return timer(config.due_time ?? 0, config.polling_interval).pipe(
    switchMap(check),
    takeWhile(config.take_while, config.inclusive),
    take(config.max_attempts),
    last()
  );
}
